import React, { useState, useCallback, useEffect, useRef } from "react";
import { RgbaColorPicker } from "react-colorful";
import { Box, Button, Popover, TextField } from "@material-ui/core";
import ColorLensIcon from "@material-ui/icons/ColorLens";
import { normalizeHexInput } from "./colorUtils";

const parseRgba = (str) => {
	const m = str && str.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
	if (m) return { r: +m[1], g: +m[2], b: +m[3], a: m[4] !== undefined ? +m[4] : 1 };
	return { r: 100, g: 100, b: 255, a: 0.8 };
};

const toRgba = (r, g, b, a) =>
	`rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${a})`;

const hexToRgba = (hex) => {
	hex = (hex || "").replace(/^#/, "");
	if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
	if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return null;
	const r = parseInt(hex.slice(0, 2), 16);
	const g = parseInt(hex.slice(2, 4), 16);
	const b = parseInt(hex.slice(4, 6), 16);
	return { r, g, b, a: 1 };
};

const rgbaToHex = (str) => {
	const m = str && str.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
	if (!m) return "#ffffff";
	const r = (+m[1]).toString(16).padStart(2, "0");
	const g = (+m[2]).toString(16).padStart(2, "0");
	const b = (+m[3]).toString(16).padStart(2, "0");
	return `#${r}${g}${b}`;
};

const hasEyeDropper = typeof window !== "undefined" && "EyeDropper" in window;

export default function RgbaColorPickerPopover({ value, onChange, label, compact }) {
	const [anchorEl, setAnchorEl] = useState(null);
	const [hexInput, setHexInput] = useState("");
	const popoverContentRef = useRef(null);
	const rgba = parseRgba(value);
	const hexDisplay = rgbaToHex(value);

	const handleOpen = (e) => {
		if (e && e.stopPropagation) e.stopPropagation();
		setAnchorEl(e?.currentTarget || e);
		setHexInput("");
	};
	const handleClose = useCallback(() => setAnchorEl(null), []);

	useEffect(() => {
		if (!anchorEl) return;
		const onDocMouseDown = (e) => {
			const t = e.target;
			if (anchorEl && typeof anchorEl.contains === "function" && anchorEl.contains(t)) return;
			const root = popoverContentRef.current;
			if (root && root.contains(t)) return;
			handleClose();
		};
		document.addEventListener("mousedown", onDocMouseDown, true);
		return () => document.removeEventListener("mousedown", onDocMouseDown, true);
	}, [anchorEl, handleClose]);

	const handleChange = useCallback(
		(col) => onChange(toRgba(col.r, col.g, col.b, col.a)),
		[onChange]
	);

	useEffect(() => {
		setHexInput("");
	}, [value]);

	const handleHexInputChange = useCallback((e) => {
		const v = e.target.value;
		setHexInput(v);
		const normalized = normalizeHexInput(v);
		if (normalized) {
			const col = hexToRgba(normalized);
			if (col) onChange(toRgba(col.r, col.g, col.b, 1));
		}
	}, [onChange]);

	const handleHexInputBlur = useCallback(() => {
		const normalized = normalizeHexInput(hexInput);
		if (normalized) {
			const col = hexToRgba(normalized);
			if (col) onChange(toRgba(col.r, col.g, col.b, 1));
		}
		setHexInput("");
	}, [hexInput, onChange]);

	const displayHex = hexInput !== "" ? hexInput : hexDisplay;

	const handleEyeDropper = useCallback(async () => {
		if (!hasEyeDropper) return;
		try {
			const dropper = new window.EyeDropper();
			const result = await dropper.open();
			if (result?.sRGBHex) {
				const col = hexToRgba(result.sRGBHex);
				onChange(toRgba(col.r, col.g, col.b, 1));
			}
		} catch (e) {
			// User cancelled or API failed
		}
	}, [onChange]);

	const trigger = compact ? (
		<Box
			data-er-editor-ui="1"
			onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
			onClick={handleOpen}
			title="Color del texto"
			style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", padding: "4px 6px", borderRadius: 4 }}
		>
			<span style={{ fontSize: 16, fontWeight: 600, color: "#374151", fontFamily: "Arial, sans-serif" }}>A</span>
			<Box style={{ width: 14, height: 3, borderRadius: 2, backgroundColor: value || "#000", marginTop: 1 }} />
		</Box>
	) : (
		<Box display="flex" alignItems="center" style={{ gap: 8 }}>
			<Box onClick={handleOpen} title="Abrir selector de color" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='16' height='16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h8v8H0zm8 8h8v8H8z' fill='%23ddd'/%3E%3C/svg%3E")`, border: "2px solid #e2e8f0", borderRadius: "50%", width: 36, height: 36, cursor: "pointer", flexShrink: 0, overflow: "hidden", boxSizing: "border-box" }}>
				<Box style={{ width: "100%", height: "100%", borderRadius: "50%", backgroundColor: value || "rgba(100,100,255,0.8)" }} />
			</Box>
			<TextField
				size="small"
				placeholder="#HEX"
				value={displayHex}
				onChange={handleHexInputChange}
				onBlur={handleHexInputBlur}
				onFocus={() => setHexInput(hexDisplay)}
				inputProps={{ style: { fontFamily: "monospace", fontSize: 13 }, maxLength: 9 }}
				style={{ minWidth: 88 }}
			/>
		</Box>
	);

	return (
		<Box>
			{label && !compact && <Box fontSize={12} fontWeight="bold" mb={0.5}>{label}</Box>}
			{trigger}
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				PaperProps={{ "data-er-editor-ui": "1", style: { borderRadius: 12, overflow: "visible" } }}
			>
				<Box
					ref={popoverContentRef}
					data-er-editor-ui="1"
					onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
					p={1.5}
					display="flex"
					flexDirection="column"
					alignItems="center"
				>
					<Box
						style={{
							height: 120,
							width: 160,
							borderRadius: 10,
							overflow: "hidden",
						}}
					>
						<RgbaColorPicker color={rgba} onChange={handleChange} style={{ width: "100%", height: "100%" }} />
					</Box>
					<TextField
						size="small"
						placeholder="#HEX"
						value={hexDisplay}
						onChange={(e) => {
							const normalized = normalizeHexInput(e.target.value);
							if (normalized) {
								const col = hexToRgba(normalized);
								if (col) onChange(toRgba(col.r, col.g, col.b, 1));
							}
						}}
						inputProps={{ style: { fontFamily: "monospace", fontSize: 12 }, maxLength: 9 }}
						style={{ width: "100%", marginTop: 8 }}
					/>
					{hasEyeDropper && (
						<Button
							size="small"
							startIcon={<ColorLensIcon />}
							onClick={() => {
								handleEyeDropper();
								handleClose();
							}}
							style={{ marginTop: 8 }}
						>
							Cuentagotas
						</Button>
					)}
				</Box>
			</Popover>
		</Box>
	);
}
